/**
 * Per-city local phone numbers (single source of truth).
 *
 * Phase 3 (this branch): Sydney and Brisbane/Gold Coast carry their real
 * direct-to-sales DIDs. Deploy is gated on the office confirming the DIDs
 * answer direct to sales with no early media, and must ship as a full build
 * plus PM2 restart (clears the in-memory Nitro SWR cache; see the plan's
 * go-live deploy mechanics). Melbourne stays a 1300 placeholder until its 03
 * DID exists.
 *
 * The `display` string is pushed to the dataLayer (`local_phone`) and feeds the
 * GTM Calls-from-Website tag's "Displayed Phone Number to Replace" field, which
 * requires an exact-digit match against the rendered number. Keep `display` and
 * what the page renders coupled.
 */
export type PhoneRegion = 'national' | 'sydney' | 'brisbane' | 'goldcoast' | 'melbourne'

export const PHONE_REGIONS: Record<PhoneRegion, { display: string; tel: string }> = {
  national: { display: '1300 300 210', tel: '1300300210' },
  sydney: { display: '02 8002 0955', tel: '0280020955' },
  brisbane: { display: '07 3015 1309', tel: '0730151309' },
  goldcoast: { display: '07 3015 1309', tel: '0730151309' }, // shared with Brisbane
  melbourne: { display: '1300 300 210', tel: '1300300210' }, // placeholder until an 03 DID exists
}

// Allowlist, NOT substring matching. Only city pages under /security-services/
// resolve non-national, which keeps blog/help-centre/checkout/billing national
// automatically (e.g. the live blog URL containing "gold-coast" stays national).
const CITY_SUFFIX: [string, PhoneRegion][] = [
  ['-gold-coast', 'goldcoast'],
  ['-sydney', 'sydney'],
  ['-brisbane', 'brisbane'],
  ['-melbourne', 'melbourne'],
]

// Service-area pages, including the qldmetwide-takeover redirect targets:
// Ipswich and Tweed Heads share the Brisbane/Gold Coast 07 DID (Tweed Heads
// sits in the 07 area-code region despite the NSW border). Unknown cities
// stay national.
const SERVICE_AREA_REGION: Record<string, PhoneRegion> = {
  sydney: 'sydney',
  brisbane: 'brisbane',
  'gold-coast': 'goldcoast',
  melbourne: 'melbourne',
  ipswich: 'brisbane',
  'tweed-heads': 'goldcoast',
}

export function resolveRegion(path: string): PhoneRegion {
  const p = path.toLowerCase().replace(/\/+$/, '')
  if (!p.startsWith('/security-services/')) return 'national'
  const sa = p.match(/\/service-areas\/([a-z-]+)$/)
  if (sa) return SERVICE_AREA_REGION[sa[1] ?? ''] ?? 'national'
  return CITY_SUFFIX.find(([suffix]) => p.endsWith(suffix))?.[1] ?? 'national'
}

/**
 * Format a raw AU number for display: 1300/1800/mobile as "XXXX XXX XXX",
 * local landlines (02/03/07/08) as "0X XXXX XXXX". Non-10-digit input is
 * returned as given.
 */
export function formatAuPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length !== 10) return raw
  if (digits.startsWith('1300') || digits.startsWith('1800') || digits.startsWith('04')) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
  }
  if (/^0[2378]/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`
  }
  return raw
}
